// ============================================================
// CustomModifier - Arbitrary named attack modifiers.
//
// Inline "add modifier" row with label + value inputs.
// Active modifiers shown as removable chips.
// ============================================================
import { useState } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/CustomModifier.module.css';
import { loadModifierPresets, saveModifierPreset, deleteModifierPreset } from '../utils/modifierPresets';
import type { ModifierPreset } from '../utils/modifierPresets';
import { activatable, onActivateKey } from '../utils/keyActivation';

/**
 * The common situations, one click each. Labels never embed the value -
 * every renderer (palette chip, active chip, ledger, log) appends the
 * signed number itself, so an embedded one printed twice (v8.228.0).
 */
const BUILTIN_PALETTE: ModifierPreset[] = [
  { label: 'Situational', value: 10 },
  { label: 'Situational', value: -10 },
  { label: 'Situational', value: 20 },
  { label: 'Situational', value: -20 },
  { label: 'Cover', value: -20 },
  { label: 'Higher Ground', value: 10 },
  { label: 'Darkness', value: -20 },
];

export interface CustomMod {
  id: string;
  label: string;
  value: number;
}

interface CustomModifierProps {
  modifiers: CustomMod[];
  onAdd: (mod: CustomMod) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

let nextId = 1;

/** The same situation: label and value both match. */
const sameMod = (a: ModifierPreset, b: ModifierPreset) => a.label === b.label && a.value === b.value;

/** "+10" / "-20": the signed value every renderer appends to a label. */
const sign = (v: number) => (v > 0 ? '+' : '');

/** The built-ins, then the user's saved presets that are not one of them. */
function paletteOf(presets: ModifierPreset[]): ModifierPreset[] {
  return [...BUILTIN_PALETTE, ...presets.filter((p) => !BUILTIN_PALETTE.some((b) => sameMod(b, p)))];
}

export default function CustomModifier({ modifiers, onAdd, onRemove, disabled }: CustomModifierProps) {
  const [presets, setPresets] = useState<ModifierPreset[]>(() => loadModifierPresets());
  /** Save a situation to the palette, or forget it if it is already there. */
  const toggleSaved = (mod: ModifierPreset) =>
    setPresets(
      presets.some((p) => sameMod(p, mod))
        ? deleteModifierPreset(mod.label, mod.value)
        : saveModifierPreset({ label: mod.label, value: mod.value }),
    );
  return (
    <div className={styles.container}>
      {/* The palette: built-ins plus the user's saved presets (v8.223.0). */}
      {!disabled && (
        <ModPalette
          modifiers={modifiers}
          presets={presets}
          onAdd={onAdd}
          onRemove={onRemove}
          onDeletePreset={(p) => setPresets(deleteModifierPreset(p.label, p.value))}
        />
      )}
      {modifiers.length > 0 && (
        <div className={styles.chipRow}>
          {modifiers.map((mod) => (
            <ActiveModChip
              key={mod.id}
              mod={mod}
              saved={presets.some((p) => sameMod(p, mod))}
              disabled={disabled}
              onToggleSaved={() => toggleSaved(mod)}
              onRemove={() => onRemove(mod.id)}
            />
          ))}
        </div>
      )}
      <AddModControl onAdd={onAdd} disabled={disabled} />
    </div>
  );
}

interface ModPaletteProps {
  modifiers: CustomMod[];
  presets: ModifierPreset[];
  onAdd: (mod: CustomMod) => void;
  onRemove: (id: string) => void;
  onDeletePreset: (preset: ModifierPreset) => void;
}

/** One click per situation: an active identical mod comes off, otherwise it goes on. Right-click forgets a saved one. */
function ModPalette({ modifiers, presets, onAdd, onRemove, onDeletePreset }: ModPaletteProps) {
  const toggle = (preset: ModifierPreset) => {
    const active = modifiers.find((m) => sameMod(m, preset));
    if (active) onRemove(active.id);
    else onAdd({ id: 'cmod-' + nextId++, label: preset.label, value: preset.value });
  };
  return (
    <div className={styles.paletteRow} data-testid="mod-palette">
      {paletteOf(presets).map((preset) => (
        <PaletteChip
          key={`${preset.label}|${preset.value}`}
          preset={preset}
          active={modifiers.some((m) => sameMod(m, preset))}
          saved={presets.some((p) => sameMod(p, preset))}
          onToggle={() => toggle(preset)}
          onDelete={() => onDeletePreset(preset)}
        />
      ))}
    </div>
  );
}

interface PaletteChipProps {
  preset: ModifierPreset;
  active: boolean;
  /** A saved preset (not a built-in) - right-click forgets it. */
  saved: boolean;
  onToggle: () => void;
  onDelete: () => void;
}

function PaletteChip({ preset, active, saved, onToggle, onDelete }: PaletteChipProps) {
  const what = `${preset.label} ${sign(preset.value)}${preset.value} - click to ${active ? 'remove' : 'apply'}`;
  return (
    <button
      type="button"
      className={active ? styles.paletteChipActive : styles.paletteChip}
      aria-pressed={active}
      title={what + (saved ? '; right-click to delete the preset' : '')}
      onClick={onToggle}
      onContextMenu={(e) => {
        if (!saved) return;
        e.preventDefault();
        onDelete();
      }}
    >
      {preset.label} {sign(preset.value)}
      {preset.value}
    </button>
  );
}

interface ActiveModChipProps {
  mod: CustomMod;
  saved: boolean;
  disabled?: boolean;
  onToggleSaved: () => void;
  onRemove: () => void;
}

/** An active modifier: its signed value, a star to keep it as a preset, and a cross to drop it. */
function ActiveModChip({ mod, saved, disabled, onToggleSaved, onRemove }: ActiveModChipProps) {
  return (
    <span className={styles.chip} style={{ color: mod.value > 0 ? 'var(--ok-soft)' : 'var(--danger-soft)' }}>
      {mod.label} {sign(mod.value)}
      {mod.value}
      {!disabled && (
        <>
          <span
            className={styles.chipStar}
            role="button"
            tabIndex={0}
            aria-label={saved ? `Forget preset ${mod.label}` : `Save ${mod.label} as a preset`}
            title={saved ? 'Forget this preset' : 'Save to your palette - it survives reloads'}
            onClick={onToggleSaved}
            onKeyDown={onActivateKey(onToggleSaved)}
          >
            {saved ? '\u2605' : '\u2606'}
          </span>
          <span className={styles.chipRemove} {...activatable(onRemove)}>
            {'\u2715'}
          </span>
        </>
      )}
    </span>
  );
}

/**
 * "+ Custom Mod", which opens a label + value row. The drafts survive
 * closing it (Escape or the cross); the refusal is cleared by the cross.
 */
function AddModControl({ onAdd, disabled }: { onAdd: (mod: CustomMod) => void; disabled?: boolean }) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const add = () => {
    const numVal = parseInt(value) || 0;
    // A zero modifier is refused OUT LOUD - the old gate returned in
    // silence and the Add button read as broken (v8.221.0). The label is
    // optional now: an unnamed bonus auto-names itself.
    if (numVal === 0) {
      setProblem('The value cannot be 0 - that is not a modifier.');
      return;
    }
    onAdd({ id: 'cmod-' + nextId++, label: label.trim() || 'Situational', value: numVal });
    setLabel('');
    setValue('');
    setProblem(null);
    setAdding(false);
  };
  if (!adding) {
    return (
      <button
        className={common.btnSmall}
        style={{ fontSize: 10, marginTop: 2 }}
        onClick={() => setAdding(true)}
        disabled={disabled}
      >
        + Custom Mod
      </button>
    );
  }
  const cancel = () => {
    setAdding(false);
    setProblem(null);
  };
  const draft = { label, setLabel, value, setValue, problem };
  return <AddModRow draft={draft} onAdd={add} onEscape={() => setAdding(false)} onCancel={cancel} />;
}

interface AddModRowProps {
  draft: {
    label: string;
    setLabel: (label: string) => void;
    value: string;
    setValue: (value: string) => void;
    problem: string | null;
  };
  onAdd: () => void;
  onEscape: () => void;
  onCancel: () => void;
}

function AddModRow({ draft, onAdd, onEscape, onCancel }: AddModRowProps) {
  return (
    <div className={styles.addRow}>
      <input
        className={common.input}
        style={{ width: 70, fontSize: 10 }}
        placeholder="Label (optional)"
        value={draft.label}
        onChange={(e) => draft.setLabel(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onAdd()}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the add row appears on the user's own click
        autoFocus
      />
      <input
        className={common.input}
        style={{ width: 40, fontSize: 10, textAlign: 'center' }}
        placeholder="+/-"
        type="number"
        value={draft.value}
        onChange={(e) => draft.setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onAdd();
          if (e.key !== 'Escape') return;
          // Stop here: the same key would otherwise close the panel the row sits in.
          e.stopPropagation();
          onEscape();
        }}
      />
      <button className={common.btnSmall} style={{ fontSize: 10 }} onClick={onAdd}>
        Add
      </button>
      <button className={common.btnSmall} style={{ fontSize: 10, color: 'var(--danger)' }} onClick={onCancel}>
        {'\u2715'}
      </button>
      {draft.problem && (
        <span className={styles.problem} role="alert">
          {draft.problem}
        </span>
      )}
    </div>
  );
}
